import { Injectable } from '@angular/core';
import { AppInfoService } from 'core/app-info/app-info.service';
import { CartItem } from '../entities/cart-item';
import { B2bCartNotification, CartNotificationAction, CartNotificationRule, CartNotificationType } from '../entities/b2b-cart-notification';

@Injectable({
  providedIn: 'root'
})
export class B2bCartNotificationService {
  constructor(private appInfoService: AppInfoService) {}

  appStrings = this.appInfoService.appStrings;

  buildNotifications(items: CartItem[] = []): B2bCartNotification[] {
    if (!items?.length) {
      return [];
    }

    return items
      .map((item) => this.buildNotificationForItem(item))
      .filter((notification): notification is B2bCartNotification => Boolean(notification));
  }

  private buildNotificationForItem(item: CartItem): B2bCartNotification | null {
    if (!item?.ModelNumber) {
      return null;
    }

    const itemNumber = item.ModelNumber;
    const supersedeItem = this.getSupersedeItem(item);
    const hasSupersede = this.hasSupersede(item);
    const isNewProduct = this.isNewProduct(item);
    const isDiscontinued = this.isDiscontinued(item);
    const isRestricted = this.isRestricted(item);
    const isLimitedMarketing = this.isLimitedMarketing(item);
    const priceMissing = this.isPriceMissing(item);
    const priceIsZero = this.isPriceZero(item);
    const priceExpired = this.isPriceExpired(item);

    if (this.isProductUnavailable(item)) {
      return hasSupersede
        ? this.createNotification({
          rule: CartNotificationRule.ProductNotFoundWithSupersede,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productNotFoundWithSupersede
            ?.replaceAll('{0}', itemNumber)
            .replaceAll('{1}', supersedeItem) ?? '',
          actions: this.actions(
            this.appStrings.button_remove as CartNotificationAction,
            this.appStrings.button_replace as CartNotificationAction
          ),
          itemNumber
        })
        : this.createNotification({
          rule: CartNotificationRule.ProductNotFound,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productNotFoundTitle ?? '',
          actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
          itemNumber
        });
    }

    if (isNewProduct) {
      return hasSupersede
        ? this.createNotification({
          rule: CartNotificationRule.ProductIsNewWithSupersede,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productIsNewWithSupersede
            ?.replaceAll('{0}', itemNumber)
            .replaceAll('{1}', supersedeItem) ?? '',
          actions: this.actions(
            this.appStrings.button_remove as CartNotificationAction,
            this.appStrings.button_replace as CartNotificationAction
          ),
          itemNumber
        })
        : this.createNotification({
          rule: CartNotificationRule.ProductIsNew,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productIsNew ?? '',
          actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
          itemNumber
        });
    }

    if (isDiscontinued) {
      return hasSupersede
        ? this.createNotification({
          rule: CartNotificationRule.ProductDiscontinuedWithSupersede,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productDiscontinuedWithSupersede
            ?.replaceAll('{0}', itemNumber)
            .replaceAll('{1}', supersedeItem) ?? '',
          actions: this.actions(
            this.appStrings.button_remove as CartNotificationAction,
            this.appStrings.button_replace as CartNotificationAction
          ),
          itemNumber
        })
        : this.createNotification({
          rule: CartNotificationRule.ProductDiscontinued,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productDiscontinuedTitle ?? '',
          description: this.appStrings.b2bCart_notification_productDiscontinuedDescription ?? '',
          actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
          itemNumber
        });
    }

    if (isRestricted) {
      return hasSupersede
        ? this.createNotification({
          rule: CartNotificationRule.ProductRestrictedWithSupersede,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productRestrictedWithSupersede
            ?.replaceAll('{0}', itemNumber)
            .replaceAll('{1}', supersedeItem) ?? '',
          actions: this.actions(
            this.appStrings.button_remove as CartNotificationAction,
            this.appStrings.button_replace as CartNotificationAction
          ),
          itemNumber
        })
        : this.createNotification({
          rule: CartNotificationRule.ProductRestricted,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_productRestricted ?? '',
          actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
          itemNumber
        });
    }

    if (!isLimitedMarketing && !isDiscontinued && priceMissing) {
      return this.createNotification({
        rule: CartNotificationRule.PriceNotFound,
        type: CartNotificationType.Error,
        title: this.appStrings.b2bCart_notification_priceNotFoundTitle ?? '',
        actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
        itemNumber
      });
    }

    if (!isLimitedMarketing && !isDiscontinued && priceIsZero) {
      return this.createNotification({
        rule: CartNotificationRule.PriceIsZero,
        type: CartNotificationType.Error,
        title: this.appStrings.b2bCart_notification_priceIsZeroTitle ?? '',
        actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
        itemNumber
      });
    }

    if (priceExpired) {
      return hasSupersede
        ? this.createNotification({
          rule: CartNotificationRule.PriceExpiredWithSupersede,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_priceExpiredWithSupersedeTitle ?? '',
          actions: this.actions(
            this.appStrings.button_remove as CartNotificationAction,
            this.appStrings.button_replace as CartNotificationAction
          ),
          itemNumber
        })
        : this.createNotification({
          rule: CartNotificationRule.PriceExpired,
          type: CartNotificationType.Error,
          title: this.appStrings.b2bCart_notification_priceExpiredTitle ?? '',
          actions: this.actions(this.appStrings.button_remove as CartNotificationAction),
          itemNumber
        });
    }

    if (this.shouldWarnSupersede(item)) {
      return this.createNotification({
        rule: CartNotificationRule.SupersedeWarning,
        type: CartNotificationType.Warning,
        title: this.appStrings.b2bCart_notification_supersedeWarning
          ?.replaceAll('{0}', itemNumber)
          .replaceAll('{1}', supersedeItem) ?? '',
        actions: this.actions(
          this.appStrings.button_replace as CartNotificationAction,
          this.appStrings.button_keep as CartNotificationAction
        ),
        itemNumber
      });
    }

    return null;
  }

  private hasSupersede(item: CartItem): boolean {
    return Boolean(item?.SuperseededBy);
  }

  private getSupersedeItem(item: CartItem): string {
    return (item?.SuperseededBy ?? '').toString();
  }

  private isProductUnavailable(item: CartItem): boolean {
    const activeFlag = this.toBoolean(item?.Active);
    return item?.ProductFound === false && activeFlag === false;
  }

  private isNewProduct(item: CartItem): boolean {
    return this.toNumber(item?.LifeCycleId) === 1;
  }

  private isDiscontinued(item: CartItem): boolean {
    return this.toNumber(item?.LifeCycleId) === 5;
  }

  private isFutureDiscontinued(item: CartItem): boolean {
    return this.toNumber(item?.LifeCycleId) === 4;
  }

  private isLimitedMarketing(item: CartItem): boolean {
    const lmCode = this.toNumber(item?.LmCode);
    const limitedMarketingFlag = this.toBoolean(item?.IsLimitedMarketing);

    if (lmCode === 4) {
      return true;
    }

    if ((lmCode === 2 || lmCode === 3) && limitedMarketingFlag === true) {
      return true;
    }

    return false;
  }

  private isRestricted(item: CartItem): boolean {
    return !this.isDiscontinued(item) && this.isLimitedMarketing(item);
  }

  private isPriceMissing(item: CartItem): boolean {
    return this.toNumber(item?.NetPrice) === null;
  }

  private isPriceZero(item: CartItem): boolean {
    const netPrice = this.toNumber(item?.NetPrice);
    return netPrice === 0;
  }

  private isPriceExpired(item: CartItem): boolean {
    const signalCode = item?.SignalCode ? item.SignalCode.toString().trim() : '';
    return signalCode === '041' && !this.isDiscontinued(item) && !this.isLimitedMarketing(item);
  }

  private shouldWarnSupersede(item: CartItem): boolean {
    return this.isFutureDiscontinued(item) && this.hasSupersede(item) || this.hasSupersede(item);
  }

  private toNumber(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  private toBoolean(value: unknown): boolean | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (value === '1' || value === 1) {
      return true;
    }

    if (value === '0' || value === 0) {
      return false;
    }

    return null;
  }

  private actions(...labels: CartNotificationAction[]): CartNotificationAction[] {
    return [...labels];
  }

  private createNotification(config: {
    rule: CartNotificationRule;
    type: CartNotificationType;
    title: string;
    itemNumber: string;
    actions: CartNotificationAction[];
    description?: string;
  }): B2bCartNotification {
    return {
      rule: config.rule,
      type: config.type,
      title: config.title,
      description: config.description,
      actions: [],
      itemNumber: config.itemNumber
    };
  }
}
